import axios from 'axios';
import type { AxiosError, CancelTokenSource } from 'axios';

import type { SignUpAdvanceRequest } from 'models/riskos';

import type { ApiErrorType, ServerError } from '../../models';
import type {
    AdvanceSignUpResponse,
    DuplicateAccountRecoveryDocvTokenResponse,
    DuplicateAccountRecoveryRequest,
    DuplicateAccountRecoveryResponse,
    ExecuteDocumentVerificationRequest,
    ExecuteKycRequest,
    ExecuteKycResponse,
    GetLocationResponse,
    GetUserSignupResponse,
    SendSignupEmailVerificationRequest,
    SendSignupEmailVerificationResponse,
    SignUpParams,
    SignupRequest,
    SignupResponse,
    SignupVerifiedRequest,
    SignupVerifiedResponse,
    SignupWithReferralRequest,
    VerifyPhoneRequest,
    VerifyPhoneResponse,
    VerifySignupEmailRequest,
    VerifySignupEmailResponse,
} from '../../models/reqres';
import { buildQueryString } from '../../utils/query';
import { exchangeApi, handleErrorCallback, noop } from '../core';

export function signUp(
    payload: SignupRequest,
    onSuc: (resp: SignupResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
    params?: SignUpParams,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<SignupResponse>(`/users/${buildQueryString(params)}${params ? '&' : '?'}four_digit_code=true`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

// Pre-signup email verification. Response is uniform whether or not an account exists.
export function sendSignupEmailVerification(
    payload: SendSignupEmailVerificationRequest,
    onSuc: (resp: SendSignupEmailVerificationResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<SendSignupEmailVerificationResponse>(`/signup/email_verification?four_digit_code=true`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function verifySignupEmail(
    payload: VerifySignupEmailRequest,
    onSuc: (resp: VerifySignupEmailResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<VerifySignupEmailResponse>(`/signup/email_verification/verify`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function signUpVerified(
    payload: SignupVerifiedRequest,
    onSuc: (resp: SignupVerifiedResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
    params?: SignUpParams,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<SignupVerifiedResponse>(`/signup/verified${buildQueryString(params)}`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function postSignupReferralCode(
    payload: SignupWithReferralRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
    onLog?: (err: ServerError) => void,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<void>(`/referral_signup`, payload, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr, onLog);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function sendSignupLink(
    email: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<SignupResponse>('/resume_sign_up?four_digit_code=true', { email }, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function sendResetPasswordLink(
    email: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
    sardineSessionKey?: string,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(
            `/passwords/reset`,
            { email, sardine_session_key: sardineSessionKey || undefined },
            { cancelToken: cancelRef.token },
        )
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function verifyPasswordReset(
    userId: string,
    code: string,
    password: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .put(`/passwords/reset/${code}/confirm`, { user_id: userId, password }, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getUserSignup(
    allowHidden: boolean,
    userId: string,
    onSuc: (signupResponse: GetUserSignupResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    const MAX_RETRIES = 4;
    const BASE_DELAY_MS = 200;

    const getBackoffDelay = (attempt: number): number => {
        return BASE_DELAY_MS * Math.pow(2, attempt);
    };

    const makeRequest = (attempt: number): void => {
        exchangeApi
            .get<GetUserSignupResponse>(`/users/${userId}/sign_up`, {
                cancelToken: cancelRef.token,
                params: { allowRequestEvenIfPageIsHidden: allowHidden },
            })
            .then((response) => {
                onSuc(response.data);
                onFinally();
            })
            .catch((error: AxiosError<ApiErrorType>) => {
                const is404 = error.response?.status === 404;
                const shouldRetry = is404 && attempt < MAX_RETRIES;

                if (shouldRetry) {
                    setTimeout(() => {
                        makeRequest(attempt + 1);
                    }, getBackoffDelay(attempt));
                } else {
                    handleErrorCallback(error, onErr);
                    onFinally();
                }
            });
    };

    makeRequest(0);

    return cancelRef;
}

export function signUpAdvance(
    userId: string,
    payload: SignUpAdvanceRequest,
    onSuc: (response: AdvanceSignUpResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<AdvanceSignUpResponse>(`/users/${userId}/sign_up/advance`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function completeDemoSignup(
    userId: string,
    onSuc: (response: AdvanceSignUpResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<AdvanceSignUpResponse>(
            `/users/${userId}/sign_up/demo_complete`,
            {},
            {
                cancelToken: cancelRef.token,
            },
        )
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function executeDigitalKyc(
    userId: string,
    payload: ExecuteKycRequest,
    onSuc: (kycResponse: ExecuteKycResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<ExecuteKycResponse>(`/users/${userId}/sign_up/digital_verification`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function executeDocumentVerification(
    userId: string,
    payload: ExecuteDocumentVerificationRequest,
    onSuc: (kycResult: ExecuteKycResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<ExecuteKycResponse>(`/users/${userId}/sign_up/document_verification`, payload, {
            cancelToken: cancelRef.token,
            // This request can take a long time
            // But no problem if the user refresh
            // So using a long timeout here
            timeout: 60000,
            params: { allowRequestEvenIfPageIsHidden: true },
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getDuplicateAccountRecoveryDocvToken(
    userId: string,
    onSuc: (response: DuplicateAccountRecoveryDocvTokenResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<DuplicateAccountRecoveryDocvTokenResponse>(
            `/users/${userId}/sign_up/duplicate_account_recovery/docv_token`,
            {},
            { cancelToken: cancelRef.token },
        )
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function submitDuplicateAccountRecovery(
    userId: string,
    payload: DuplicateAccountRecoveryRequest,
    onSuc: (response: DuplicateAccountRecoveryResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<DuplicateAccountRecoveryResponse>(`/users/${userId}/sign_up/duplicate_account_recovery`, payload, {
            cancelToken: cancelRef.token,
            timeout: 60000,
            params: { allowRequestEvenIfPageIsHidden: true },
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function verifyPhone(
    userId: string,
    payload: VerifyPhoneRequest,
    onSuc: (response: VerifyPhoneResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<VerifyPhoneResponse>(`/users/${userId}/verify_phone`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function requestUptier(
    userId: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post(`/users/${userId}/uptier`, {}, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getLocation(
    onSuc: (location: GetLocationResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetLocationResponse>(`/location`, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
